import { FillingBatch } from '../types/fillingBatch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const fillingBatchApiService = {
  /**
   * Get the next sequential lot number for a filling batch
   */
  getNextLotNumber: async (productCode: string, dateOfProduction: string): Promise<string> => {
    const params = new URLSearchParams({ productCode, dateOfProduction });
    const response = await fetch(`${API_BASE_URL}/filling-batch/next-lot-number?${params}`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get next lot number');
    }
    return data.lotNumber;
  },

  /**
   * Fetch all filling batches from the backend
   */
  fetchFillingBatches: async (): Promise<FillingBatch[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch filling batches');
      }

      return data.fillingBatches;
    } catch (error) {
      console.error('Error fetching filling batches from API:', error);
      throw error;
    }
  },

  /**
   * Fetch a single filling batch by ID
   */
  fetchFillingBatchById: async (id: string): Promise<FillingBatch> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch filling batch');
      }

      return data.fillingBatch;
    } catch (error) {
      console.error('Error fetching filling batch by ID from API:', error);
      throw error;
    }
  },

  /**
   * Create a new filling batch
   */
  createFillingBatch: async (batch: FillingBatch): Promise<FillingBatch> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batch)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create filling batch');
      }

      return data.fillingBatch;
    } catch (error) {
      console.error('Error creating filling batch:', error);
      throw error;
    }
  },

  /**
   * Update an existing filling batch
   */
  updateFillingBatch: async (batch: FillingBatch): Promise<FillingBatch> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch/${batch.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batch)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update filling batch');
      }

      return data.fillingBatch;
    } catch (error) {
      console.error('Error updating filling batch:', error);
      throw error;
    }
  },

  /**
   * Delete a filling batch by ID
   */
  deleteFillingBatch: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete filling batch');
      }
    } catch (error) {
      console.error('Error deleting filling batch:', error);
      throw error;
    }
  }
};
